import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import IconWithBg from "ui/IconWithBg"
import BlackoutReasonChip from "ui/Blackout/BlackoutReasonChip"
import BookingsActionAlert from "ui/Blackout/BookingsActionAlert"
import { buildReservationsLink, openReservationsLink } from "helpers/blackouts"
import { buildBlackoutRuns, getDayChipProps, hasBlackoutAction, marksBookingConflicts, resolveChipLabel } from "pagesV2/pro/Calendar/blackoutUtils"
import moment from "moment"
import EventBlock from "../EventBlock"
import MultiDayStrip from "./MultiDayStrip"
import { getJournalIcon } from "../../../utils"
import { getEventColor, getEventBorderColor, overlaps } from "pagesV2/pro/Calendar/utils"
import { getDayMinWidth, getEventPosition, getLanesForDay, getOverlapLayout, HOUR_HEIGHT } from "../timeGridUtils"
import styles from "../index.module.css"

const AgendaWeekView = ({
  calendarDays,
  hours,
  totalHeight,
  startHour,
  endHour,
  gridMinWidth,
  eventsByDayWithCols,
  timeOffByDay,
  journalNoteDates,
  isCompact,
  active,
  canViewJournal,
  canCreateJournal,
  canEditJournal,
  setIsBookingModalOpen,
  setSelectedBookingId,
  onJournalOpen,
  getDayBlackout,
  getBookingConflict,
  onOpenDayPanel,
  multiDayBookings = [],
  onOpenBooking,
}) => {
  const { t } = useTranslation()
  const dayKeys = calendarDays.map((day) => day.format("YYYY-MM-DD"))
  const blackoutRuns = buildBlackoutRuns(dayKeys, getDayBlackout)
  const hasAnyBlackout = blackoutRuns.some((run) => run.day)

  /**
   * The week's own action count, summed across its days.
   *
   * Each day's chip already says what is closed; this says what it costs — the bookings left
   * needing a decision — once, above the grid, rather than seven times inside it.
   */
  const bookingsRequiringAction = dayKeys.reduce((total, dayKey) => {
    const count = getDayBlackout(dayKey)?.bookings_requiring_action
    return total + (typeof count === "number" ? count : 0)
  }, 0)

  const reviewWeek = () =>
    openReservationsLink(buildReservationsLink({ dateFrom: dayKeys[0], dateTo: dayKeys[dayKeys.length - 1] }))

  // The same rule the grid's own `gridMinWidth` uses — this header had its own copy, which drifted
  // once the overlap layout changed how much room a busy hour needs.
  const getDayWidth = (dayStr) => getDayMinWidth(getLanesForDay(eventsByDayWithCols[dayStr] || []))

  return (
    <div className={styles.nonAgendaWrapper}>
      {bookingsRequiringAction > 0 && (
        <BookingsActionAlert count={bookingsRequiringAction} onReview={reviewWeek} className={styles.weekActionAlert} />
      )}
      <table className={styles.nonAgendaTable} style={{ minWidth: gridMinWidth }}>
        <colgroup>
          <col style={{ width: 64, minWidth: 64 }} />
          {calendarDays.map((day) => {
            const dayStr = day.format("YYYY-MM-DD")
            const width = getDayWidth(dayStr)
            return <col key={dayStr} style={{ width, minWidth: width }} />
          })}
        </colgroup>
        <thead>
          <tr>
            <th className={styles.nonAgendaHeaderCell} />
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD")
              const dayLabel = isCompact ? day.format("dd")[0] : day.format("ddd")
              const hasJournalNote = journalNoteDates?.includes(dayStr)
              const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal
              return (
                <th key={dayStr} className={styles.nonAgendaHeaderCell}>
                  <Flex gap={5} align="center" justify="center">
                    {hasBlackoutAction(getDayBlackout(dayStr)) && (
                      <span className={styles.weekHeaderAlert} aria-hidden>
                        <Icon name="AlertTriangle" width={18} height={18} color="var(--color-error)" />
                      </span>
                    )}
                    <Flex vertical justify={isCompact ? "space-between" : "center"} className={`${isCompact ? "h-full" : ""} ml-auto`}>
                      <Text fw="500">{dayLabel}</Text>
                      <Text fw="500">{day.format("D")}</Text>
                    </Flex>
                    {!isCompact && hasJournalPermission && (
                      <Flex className="ml-auto" align="center">
                      <IconWithBg disabled={!active} icon={getJournalIcon(journalNoteDates, dayStr)} onClick={active ? () => onJournalOpen(dayStr) : undefined} />
                      </Flex>
                    )}
                  </Flex>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {/* Blackout bars sit above the time grid; the leading cell keeps them clear of
              the hour-label column. */}
          {hasAnyBlackout && (
            <tr>
              <td className={styles.weekBlackoutBannerCell} />
              {blackoutRuns.map((run) => {
                const chip = run.day && getDayChipProps(run.day)
                return (
                  <td key={run.dateKey} colSpan={run.span} className={styles.weekBlackoutBannerCell}>
                    {chip && (
                      <BlackoutReasonChip
                        label={resolveChipLabel(chip, t)}
                        variant={chip.variant}
                        count={chip.count}
                        showCount={chip.showCount}
                        onClick={onOpenDayPanel ? () => onOpenDayPanel(run.dateKey) : undefined}
                      />
                    )}
                  </td>
                )
              })}
            </tr>
          )}
          {/* Multi-day trips get their own lanes above the hours — see MultiDayStrip. */}
          <MultiDayStrip
            dayKeys={dayKeys}
            bookings={multiDayBookings}
            onOpenBooking={onOpenBooking}
            isBlackedOut={(dayKey) => Boolean(getDayBlackout(dayKey))}
          />
          {/* Invisible first row that holds the absolute-positioned event layers per day */}
          <tr style={{ height: 0 }}>
            <td />
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD")
              const eventsWithCols = eventsByDayWithCols[dayStr] || []
              const timeOffForDay = timeOffByDay[dayStr]
              // A day with a blackout chip already names the time off ("Crew OFF"), so its overlay
              // is left out; bookings inside the time off are still marked below.
              const showsTimeOff = !getDayBlackout(dayStr)
              return (
                <td key={dayStr} style={{ padding: 0, position: "relative", height: 0 }}>
                  {/* This absolutely-positioned layer covers the full column height */}
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: totalHeight, pointerEvents: "none" }}>
                  {getDayBlackout(dayStr) && (
                    <div
                      aria-hidden
                      className={`${styles.agendaBlackoutOverlay} ${getDayBlackout(dayStr).status === "full" ? "" : styles.agendaBlackoutOverlayPartial}`}
                    />
                  )}
                  {showsTimeOff && timeOffForDay?.allDay && <div className={`${styles.timeOffOverlay} ${styles.timeOffOverlayFull}`} style={{ pointerEvents: "auto" }} />}
                  {showsTimeOff && !timeOffForDay?.allDay && (timeOffForDay?.ranges || []).map((toff) => {
                        const topPx = Math.max(0, (toff.start.hour() - startHour) * 60 + toff.start.minute()) * (HOUR_HEIGHT / 60)
                        const botPx = Math.max(0, (toff.end.hour() - startHour) * 60 + toff.end.minute()) * (HOUR_HEIGHT / 60)
                    return <div key={`${dayStr}-${toff.start.format("HH:mm")}`} className={styles.timeOffOverlay} style={{ top: topPx, height: Math.max(0, botPx - topPx), pointerEvents: "auto" }} />
                      })}
                    {eventsWithCols.map((ev) => {
                      const pos = getEventPosition(ev, startHour, endHour)
                      if (!pos) return null
                      const overlap = getOverlapLayout(ev, pos.height)
                    const conflict = timeOffForDay?.allDay || (timeOffForDay?.ranges || []).some((r) => overlaps(ev.startMoment, ev.endMoment, r.start, r.end))
                      return (
                      <div key={ev.id ?? ev.title + ev.start} className={`${styles.eventBlockPositioned} ${overlap.isCascaded ? styles.eventBlockStacked : ""}`} style={{ top: pos.top + overlap.top, height: overlap.height, left: overlap.left, width: overlap.width, zIndex: overlap.zIndex, pointerEvents: "auto" }}>
                        <EventBlock event={ev} eventColor={getEventColor(ev.payment_status, ev.booking_status)} eventBorderColor={getEventBorderColor(ev.payment_status)} conflict={conflict} isBlackoutConflict={marksBookingConflicts(getDayBlackout(dayStr)) && Boolean(getBookingConflict(ev))} setIsBookingModalOpen={setIsBookingModalOpen} setSelectedBookingId={setSelectedBookingId} fullHeight />
                        </div>
                      )
                    })}
                  </div>
                </td>
              )
            })}
          </tr>
          {/* Hour rows — purely for the grid lines and time labels */}
          {hours.map((hour, hIdx) => {
            const isLast = hIdx === hours.length - 1
            return (
              <tr key={hour} style={{ height: isLast ? undefined : HOUR_HEIGHT }}>
                <td className={styles.timeLabelCell}>
                  <Text fw="500">{moment(hour, "H").format("h A")}</Text>
                </td>
                {calendarDays.map((day) => (
                  <td
                    key={day.format("YYYY-MM-DD")}
                    className={styles.nonAgendaDummyCell}
                    style={isLast ? { minHeight: HOUR_HEIGHT } : undefined}
                  />
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

AgendaWeekView.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  hours: PropTypes.array.isRequired,
  totalHeight: PropTypes.number.isRequired,
  startHour: PropTypes.number.isRequired,
  endHour: PropTypes.number,
  gridMinWidth: PropTypes.number.isRequired,
  eventsByDayWithCols: PropTypes.object.isRequired,
  timeOffByDay: PropTypes.object.isRequired,
  journalNoteDates: PropTypes.array,
  isCompact: PropTypes.bool,
  active: PropTypes.bool,
  canViewJournal: PropTypes.bool,
  canCreateJournal: PropTypes.bool,
  canEditJournal: PropTypes.bool,
  setIsBookingModalOpen: PropTypes.func.isRequired,
  setSelectedBookingId: PropTypes.func.isRequired,
  onJournalOpen: PropTypes.func.isRequired,
  /** Looks a "YYYY-MM-DD" up in the blackout feed. */
  getDayBlackout: PropTypes.func.isRequired,
  /** Returns the blackout a booking clashes with, or null. */
  getBookingConflict: PropTypes.func.isRequired,
  /** Every booking in the week; the strip above the hours picks the multi-day ones out. */
  multiDayBookings: PropTypes.array,
  onOpenBooking: PropTypes.func,
  onOpenDayPanel: PropTypes.func,
}

export default AgendaWeekView
